import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Archive, Check, FileText, GraduationCap, History, Library, Loader2, Pencil, Pin, PinOff, Plus, RotateCcw,
  Sparkles, Undo2, Wand2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import MarkdownMessage from '../ai/MarkdownMessage';
import {
  PanelCard, PanelEmpty, PanelIntro, PanelList, PanelRow, PanelSection, PanelToggle, RemoveButton, panelButton, panelField, panelPrimary,
} from './panel/PanelKit';
import { useBotLearningQuery, useSkillHistoryQuery } from '../../hooks/queries';
import { useLearningAction } from '../../hooks/mutations';

const errorText = (err, t) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : t('bots.learning.failed');
};

const kindLabel = (p) => {
  if (p.kind === 'rule') return 'bots.learning.kind_rule';
  return {
    update: 'bots.learning.kind_skill_update', write_file: 'bots.learning.kind_file', archive: 'bots.learning.kind_archive',
  }[p.action] || 'bots.learning.kind_skill';
};

const Pre = ({ children }) => (
  <pre className="whitespace-pre-wrap rounded-lg bg-muted/60 p-2.5 text-xs max-h-40 overflow-y-auto">{children}</pre>
);

const Proposal = ({ p, act, busy, t }) => {
  const [open, setOpen] = useState(false);
  const before = p.action === 'write_file' ? p.previous_content : p.previous_body;
  const after = p.action === 'write_file' ? p.content : p.body;
  return (
    <PanelCard as="li" tone="attention" className="space-y-2" data-testid="bot-proposal">
      <div className="flex items-center gap-2 min-w-0">
        <span className="shrink-0 rounded-md px-1.5 py-0.5 bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold text-[11px]">
          {t(kindLabel(p))}
        </span>
        <span className="text-[13px] font-medium text-foreground truncate">{p.kind === 'rule' ? p.text : p.title || p.name}</span>
      </div>
      <div className="space-y-1 text-xs text-muted-foreground">
        {p.action === 'write_file' && <p className="font-mono">{p.file_path}</p>}
        {p.action === 'archive' && <p>{t('bots.learning.archive_into', { into: p.absorbed_into })}</p>}
        {p.kind === 'skill' && p.action !== 'archive' && p.action !== 'write_file' && <p className="text-foreground/80">{p.description}</p>}
        {p.reason && <p>{t('bots.learning.why', { reason: p.reason, bot: p.bot_name || '' })}</p>}
      </div>
      {p.kind === 'skill' && after && (
        <button type="button" className="text-xs font-medium text-foreground hover:underline underline-offset-2" onClick={() => setOpen((o) => !o)}>
          {t(open ? 'bots.learning.hide_changes' : 'bots.learning.show_changes')}
        </button>
      )}
      {open && (
        <div className="space-y-2">
          {before && (
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">{t('bots.learning.before')}</div>
              <Pre>{before}</Pre>
            </div>
          )}
          <div className="space-y-1">
            {before && <div className="text-xs text-muted-foreground">{t('bots.learning.after')}</div>}
            <Pre>{after}</Pre>
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" className={panelPrimary} disabled={busy} onClick={() => act({ action: 'accept', id: p.id })}>
          <Check className="w-3.5 h-3.5" />{t('bots.learning.accept')}
        </Button>
        <Button size="sm" variant="outline" className={panelButton} disabled={busy} onClick={() => act({ action: 'reject', id: p.id })}>
          <X className="w-3.5 h-3.5" />{t('bots.learning.reject')}
        </Button>
      </div>
    </PanelCard>
  );
};

/** One line of what bots learned lately, with Undo. */
export const learnedText = (p, t) => {
  const pre = p.status === 'pending' ? 'bots.learning.suggested_' : 'bots.learning.recent_';
  if (p.kind === 'rule') return t(`${pre}rule`, { text: p.text });
  if (p.action === 'write_file') return t(`${pre}write_file`, { name: p.name, file: p.file_path });
  if (p.action === 'archive') return t(`${pre}archive`, { name: p.name, into: p.absorbed_into });
  return t(`${pre}${p.action === 'update' ? 'update' : 'create'}`, { name: p.name });
};

const Recent = ({ rows, act, busy, t }) => (
  <PanelSection title={t('bots.learning.recent')} data-testid="learning-recent">
    <PanelList>
      {rows.map((p) => (
        <PanelRow key={p.id} wrap dimmed={p.status === 'undone'} icon={<Sparkles className="w-3.5 h-3.5 text-amber-500" />}
          title={<span className={p.status === 'undone' ? 'line-through' : ''}>{learnedText(p, t)}</span>}
          trailing={p.status === 'undone' ? (
            <span className="text-xs text-muted-foreground">{t('bots.learning.undone')}</span>
          ) : (
            <button type="button" disabled={busy} onClick={() => act({ action: 'undo', id: p.id })}
              className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50">
              <Undo2 className="w-3.5 h-3.5" />{t('bots.learning.undo')}
            </button>
          )} />
      ))}
    </PanelList>
  </PanelSection>
);

const inputClass = 'w-full text-xs rounded-md border border-border bg-transparent px-2 py-1.5 focus:outline-none focus:border-amber-500/60';

const NewSkillDialog = ({ onClose, act, busy, t }) => {
  const [form, setForm] = useState({ name: '', description: '', body: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ready = form.name.trim() && form.description.trim() && form.body.trim();
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('bots.learning.new_skill')}</DialogTitle>
          <DialogDescription>{t('bots.learning.new_skill_hint')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <input value={form.name} onChange={set('name')} maxLength={60} className={inputClass}
            placeholder={t('bots.learning.new_skill_name')} data-testid="new-skill-name" />
          <input value={form.description} onChange={set('description')} maxLength={200} className={inputClass}
            placeholder={t('bots.learning.new_skill_description')} />
          <textarea value={form.body} onChange={set('body')} rows={10} maxLength={12000}
            className={`${inputClass} font-mono resize-y`} placeholder={t('bots.learning.new_skill_body')} />
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>{t('bots.learning.cancel')}</Button>
          <Button size="sm" className="bg-amber-500 text-black hover:bg-amber-400" disabled={!ready || busy}
            onClick={() => act({ action: 'createSkill', data: form }, onClose)}>{t('bots.learning.create')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SOURCES = ['proposal', 'person', 'restore', 'undo', 'before'];

const HistoryView = ({ skill, act, busy, t, i18n, onDone }) => {
  const { data } = useSkillHistoryQuery(skill.id);
  const [open, setOpen] = useState(null);
  if (!data) return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />;
  const when = (d) => new Date(d).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <ul className="max-h-[55vh] overflow-y-auto space-y-1.5" data-testid="skill-history">
      {data.versions.map((v, i) => (
        <li key={v.id} className="rounded-md border border-border p-2 text-xs">
          <div className="flex items-center gap-2">
            <button type="button" className="flex-1 min-w-0 text-left" onClick={() => setOpen(open === v.id ? null : v.id)}>
              <span className="font-medium text-foreground">{t('bots.learning.version', { version: v.version })}</span>
              <span className="text-muted-foreground"> · {when(v.created_at)} · {t(`bots.learning.source_${SOURCES.includes(v.source) ? v.source : 'person'}`)}
                {v.by === 'auto' ? ` · ${t('bots.learning.by_auto')}` : v.by_name ? ` · ${v.by_name}` : ''}</span>
              {v.reason && <span className="block text-muted-foreground italic truncate">{v.reason}</span>}
            </button>
            {i > 0 && (
              <Button size="sm" variant="outline" className="h-6 px-2 gap-1 text-xs" disabled={busy}
                onClick={() => act({ action: 'restoreVersion', id: skill.id, data: v.id }, onDone)}>
                <RotateCcw className="w-3 h-3" />{t('bots.learning.restore_version')}
              </Button>
            )}
          </div>
          {open === v.id && <div className="mt-1.5"><Pre>{v.body}</Pre></div>}
        </li>
      ))}
    </ul>
  );
};

const SkillDialog = ({ skill, onClose, act, busy, t, i18n }) => {
  const [mode, setMode] = useState('view');
  const [form, setForm] = useState({ description: skill.description, body: skill.body });
  const [file, setFile] = useState(null);
  const files = skill.files || [];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{skill.title || skill.name}</DialogTitle>
          <DialogDescription>{skill.description} · {t('bots.learning.uses', { count: skill.uses || 0 })}</DialogDescription>
        </DialogHeader>
        {mode === 'edit' && (
          <div className="space-y-2">
            <input value={form.description} maxLength={200} className={inputClass}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            <textarea value={form.body} rows={14} maxLength={12000} className={`${inputClass} font-mono resize-y`}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} data-testid="skill-edit-body" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setMode('view')}>{t('bots.learning.cancel')}</Button>
              <Button size="sm" className="bg-amber-500 text-black hover:bg-amber-400"
                disabled={busy || !form.body.trim() || !form.description.trim()}
                onClick={() => act({ action: 'updateSkill', id: skill.id, data: form }, onClose)}>{t('bots.learning.save')}</Button>
            </div>
          </div>
        )}
        {mode === 'history' && <HistoryView skill={skill} act={act} busy={busy} t={t} i18n={i18n} onDone={onClose} />}
        {mode === 'view' && (
          <>
            <div className="max-h-[50vh] overflow-y-auto rounded-md border border-border p-3">
              <MarkdownMessage>{skill.body}</MarkdownMessage>
            </div>
            {files.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-foreground">{t('bots.learning.files')}</div>
                {files.map((f) => (
                  <div key={f.path}>
                    <button type="button" onClick={() => setFile(file === f.path ? null : f.path)}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground font-mono">
                      <FileText className="w-3 h-3" />{f.path}
                    </button>
                    {file === f.path && <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border p-2"><MarkdownMessage>{f.content}</MarkdownMessage></div>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {!skill.library && mode === 'view' && (
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="gap-1" onClick={() => setMode('edit')} data-testid="skill-edit">
              <Pencil className="w-3.5 h-3.5" />{t('bots.learning.edit')}
            </Button>
            <Button size="sm" variant="outline" className="gap-1" onClick={() => setMode('history')} data-testid="skill-history-open">
              <History className="w-3.5 h-3.5" />{t('bots.learning.history')}
            </Button>
            <Button size="sm" variant="outline" className="gap-1" disabled={busy} data-testid="skill-share"
              onClick={() => act({ action: 'shareSkill', id: skill.id }, () => { toast.success(t('bots.learning.shared')); onClose(); })}>
              <Library className="w-3.5 h-3.5" />{t('bots.learning.share')}
            </Button>
            <Button size="sm" variant="outline" className="gap-1" disabled={busy}
              onClick={() => act({ action: 'updateSkill', id: skill.id, data: { pinned: !skill.pinned } }, onClose)}>
              {skill.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              {t(skill.pinned ? 'bots.learning.unpin' : 'bots.learning.pin')}
            </Button>
            <Button size="sm" variant="outline" className="gap-1" disabled={busy}
              onClick={() => act({ action: 'updateSkill', id: skill.id, data: { status: skill.status === 'archived' ? 'active' : 'archived' } }, onClose)}>
              {skill.status === 'archived' ? <RotateCcw className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
              {t(skill.status === 'archived' ? 'bots.learning.restore' : 'bots.learning.archive')}
            </Button>
          </div>
        )}
        {mode === 'history' && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setMode('view')}>{t('bots.learning.back')}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TIDY_MIN_SKILLS = 3;

/**
 * Skills, rules and proposals of the project's bots (after Hermes Agent):
 * bots propose or learn on their own, people decide, and anything learned
 * can be undone or brought back from a skill's history.
 */
const LearningPanel = ({ projectId, onTeach }) => {
  const { t, i18n } = useTranslation();
  const { data } = useBotLearningQuery(projectId);
  const mutation = useLearningAction();
  const [rule, setRule] = useState('');
  const [skill, setSkill] = useState(null);
  const [creating, setCreating] = useState(false);
  const act = (vars, onSuccess) => mutation.mutate({ projectId, ...vars }, {
    onSuccess,
    onError: (err) => toast.error(errorText(err, t)),
  });
  if (!data) return null;
  const skills = data.skills.filter((s) => s.status !== 'archived');
  const archived = data.skills.filter((s) => s.status === 'archived');
  const recent = data.recent || [];
  const tidyNote = data.last_tidy?.result_text && data.last_tidy?.finished_at
    && Date.now() - new Date(data.last_tidy.finished_at).getTime() < 7 * 86400000 ? data.last_tidy.result_text : null;
  const allSkills = [...skills, ...archived];
  return (
    <div className="space-y-5" data-testid="bot-learning">
      <PanelIntro actions={(
        <>
          {onTeach && (
            <Button size="sm" className={panelPrimary} onClick={onTeach} title={t('bots.learning.teach_hint')} data-testid="learning-teach">
              <Sparkles className="w-3.5 h-3.5" />{t('bots.learning.teach')}
            </Button>
          )}
          <Button size="sm" variant="outline" className={panelButton} onClick={() => setCreating(true)} data-testid="learning-new-skill">
            <Plus className="w-3.5 h-3.5" />{t('bots.learning.new_skill')}
          </Button>
          {skills.length >= TIDY_MIN_SKILLS && (
            <Button size="sm" variant="outline" className={panelButton} title={t('bots.learning.tidy_hint')}
              disabled={data.tidying || mutation.isPending} data-testid="learning-tidy"
              onClick={() => act({ action: 'tidy' }, () => toast.success(t('bots.learning.tidy_started')))}>
              {data.tidying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              {t(data.tidying ? 'bots.learning.tidying' : 'bots.learning.tidy')}
            </Button>
          )}
        </>
      )} />
      <PanelToggle data-testid="auto-learn" title={t('bots.learning.auto_title')} hint={t('bots.learning.auto_hint')}
        checked={!!data.auto_learn} disabled={mutation.isPending} onChange={(v) => act({ action: 'autoLearn', data: v })} />
      {data.proposals.length > 0 && (
        <PanelSection title={t('bots.control.needs_ok')} count={data.proposals.length} tone="attention">
          <ul className="space-y-2">
            {data.proposals.map((p) => <Proposal key={p.id} p={p} act={act} busy={mutation.isPending} t={t} />)}
          </ul>
        </PanelSection>
      )}
      {tidyNote && (
        <PanelCard className="text-xs" data-testid="learning-tidy-note">
          <div className="flex items-center gap-1.5 text-[13px] font-medium text-foreground"><Wand2 className="w-3.5 h-3.5" />{t('bots.learning.tidy_done')}</div>
          <div className="text-muted-foreground mt-1 max-h-48 overflow-y-auto"><MarkdownMessage>{tidyNote}</MarkdownMessage></div>
        </PanelCard>
      )}
      {recent.length > 0 && <Recent rows={recent} act={act} busy={mutation.isPending} t={t} />}
      <PanelSection title={t('bots.learning.rules')} count={data.rules.length}>
        {data.rules.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('bots.learning.rules_empty')}</p>
        ) : (
          <PanelList>
            {data.rules.map((r) => (
              <PanelRow key={r.id} wrap title={r.text}
                trailing={<RemoveButton onClick={() => act({ action: 'deleteRule', id: r.id })} title={t('bots.memory.forget')} />} />
            ))}
          </PanelList>
        )}
        <form onSubmit={(e) => { e.preventDefault(); if (rule.trim()) act({ action: 'addRule', text: rule.trim() }, () => setRule('')); }}>
          <input value={rule} onChange={(e) => setRule(e.target.value)} maxLength={300}
            placeholder={t('bots.learning.add_rule_placeholder')} className={panelField} />
        </form>
      </PanelSection>
      <PanelSection title={t('bots.learning.skills')} count={skills.length}>
        {allSkills.length === 0 ? (
          <PanelEmpty icon={GraduationCap}>{t('bots.learning.skills_empty')}</PanelEmpty>
        ) : (
          <PanelList>
            {allSkills.map((s) => (
              <PanelRow key={s.id} onClick={() => setSkill(s)} dimmed={s.status === 'archived'}
                title={(
                  <span className="flex items-center gap-1.5 min-w-0">
                    {s.pinned && <Pin className="w-3 h-3 text-amber-500 shrink-0" />}
                    <span className="font-medium truncate">{s.title || s.name}</span>
                  </span>
                )}
                subtitle={s.description}
                trailing={(
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    {(s.files || []).length > 0 && <span className="inline-flex items-center gap-0.5"><FileText className="w-3 h-3" />{s.files.length}</span>}
                    {s.status !== 'active' && <span>{t(`bots.learning.${s.status}`)}</span>}
                  </span>
                )} />
            ))}
          </PanelList>
        )}
      </PanelSection>
      {(data.library || []).length > 0 && (
        <PanelSection title={t('bots.learning.library')} count={data.library.length} data-testid="skill-library">
          <PanelList>
            {data.library.map((s) => (
              <PanelRow key={s.id} onClick={() => setSkill({ ...s, library: true })} icon={<Library className="w-3.5 h-3.5" />}
                title={<span className="font-medium">{s.title || s.name}</span>} subtitle={s.description}
                trailing={<RemoveButton onClick={() => act({ action: 'removeLibrarySkill', id: s.id })} title={t('bots.learning.library_remove')} />} />
            ))}
          </PanelList>
        </PanelSection>
      )}
      {skill && <SkillDialog key={skill.id} skill={skill} onClose={() => setSkill(null)} act={act} busy={mutation.isPending} t={t} i18n={i18n} />}
      {creating && <NewSkillDialog onClose={() => setCreating(false)} act={act} busy={mutation.isPending} t={t} />}
    </div>
  );
};

export default LearningPanel;
